import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { formFingerprint, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"

// Singleton controller attached to #settingInfoModal. Owns the modal lifecycle
// for the in-modal App Settings edit/new flow:
//
//   - enterEdit()  → switch to edit mode: the info-mode footer actions (Close /
//                    Edit Setting) give way to the edit actions (Cancel / Save,
//                    plus "Create #channel & save" when the form offers it).
//                    Fired by the "Edit Setting" anchor, which also carries
//                    data-turbo-frame so Turbo GET-loads the form.
//   - the footer Save is the form's ONE submit button (MODAL-2): it lives
//                    outside the <turbo-frame> and reaches the form through its
//                    stable `form="app-setting-form"` association, so Enter in a
//                    field and a click both submit the current frame content
//                    and the form's own app-setting-confirm controller still
//                    intercepts the submit event. The label follows the form
//                    (`data-app-setting-form-submit-label`: Save Changes /
//                    Create Setting), and its disabled state mirrors the form's
//                    hidden webhook-test gate target, so the Test-before-Save
//                    rule for *_alert_webhook_url settings is untouched.
//                    BUTTON-9: it is also disabled until a field differs from
//                    the form as it FIRST loaded this edit session; a 422
//                    re-render never re-baselines, so unsaved edits stay dirty.
//   - createSlack() → proxies the click to the form's own (hidden)
//                    slack-channel-create button, whose controller flips the
//                    create_channel field via closest('form') and submits.
//   - app-setting-modal:done (document event) → close the modal. Dispatched by
//                    update.turbo_stream.haml after an in-place value swap.
//   - hidden.bs.modal → reset to info mode and empty the frame so the next
//                    open never shows a stale form or duplicated actions.
//
// Observation: a failed save (Admin::AppSettingsController#render_form_error)
// answers with turbo_stream.replace('app_setting_form', …), which swaps the
// WHOLE <turbo-frame> for the partial's own frame — a new node without any
// target attribute. So the observer sits on the stable frameHost wrapper (a
// body-only subtree; the footer this controller mutates is outside it, so
// syncing can never re-trigger the observer) and the live frame is always
// looked up by id, never cached.
//
// setting_info_modal_controller.js (attached per row) handles opening +
// populating; this controller handles everything once the modal is open.
export default class extends Controller {
  static targets = ["frameHost", "infoAction", "editAction", "saveButton", "slackButton"]
  static FRAME_ID = "app_setting_form"

  static WEBHOOK_GATE_HINT = "Test the webhook URL before saving"

  connect() {
    this._modal = Modal.getOrCreateInstance(this.element)

    // Guarded on isConnected: a controller whose element left the document
    // (Turbo cache, test teardown) must never act on a document-level event.
    this._boundDone = () => { if (this.element.isConnected) this._modal.hide() }
    document.addEventListener("app-setting-modal:done", this._boundDone)

    this._boundReset = () => this.reset()
    this.element.addEventListener("hidden.bs.modal", this._boundReset)

    // The frame's content changes on GET edit, the frame NODE is replaced on a
    // 422 re-render, and reset empties it; the webhook gate flips `disabled` on
    // its hidden target. One observer on the host wrapper sees all of it.
    this._observer = new MutationObserver(() => this.sync())
    this._observer.observe(this.#observedRoot(), {
      childList: true, subtree: true, attributes: true, attributeFilter: ["disabled", "hidden"],
    })
    // Typing changes values, not the DOM: the observer never sees it.
    this._boundSync = () => this.sync()
    this.#observedRoot().addEventListener("input", this._boundSync)
    this.#observedRoot().addEventListener("change", this._boundSync)
    this.sync()
  }

  disconnect() {
    document.removeEventListener("app-setting-modal:done", this._boundDone)
    this.element.removeEventListener("hidden.bs.modal", this._boundReset)
    this.#observedRoot().removeEventListener("input", this._boundSync)
    this.#observedRoot().removeEventListener("change", this._boundSync)
    this._observer?.disconnect()
  }

  enterEdit() {
    this._baseline = null
    this.element.classList.add("is-editing")
    this.sync()
  }

  // Footer proxy for the form's "Create #channel & save" secondary action.
  createSlack() {
    this.#formSlackButton()?.click()
  }

  reset() {
    this._baseline = null
    this.element.classList.remove("is-editing", "is-new")
    const frame = this.#frame()
    if (frame) {
      frame.removeAttribute("src")
      frame.innerHTML = ""
    }
    this.sync()
  }

  // Idempotent: footer state is a pure function of (mode, live form).
  sync() {
    const editing = this.element.classList.contains("is-editing")
    const form = this.#form()

    this.infoActionTargets.forEach((el) => { el.hidden = editing })
    this.editActionTargets.forEach((el) => { el.hidden = !editing })

    if (this.hasSaveButtonTarget) this.#syncSave(form, editing)
    if (this.hasSlackButtonTarget) this.#syncSlack(form, editing)
  }

  #syncSave(form, editing) {
    const save = this.saveButtonTarget
    if (form?.id) save.setAttribute("form", form.id)
    save.textContent = form?.dataset.appSettingFormSubmitLabel || "Save Changes"

    const gate = form?.querySelector('[data-webhook-test-target="saveButton"]')
    const locked = Boolean(gate?.disabled)
    const clean = Boolean(editing && form) && !this.#dirty(form)
    save.disabled = !editing || !form || locked || clean
    // BUTTON-4: a disabled control says why (tooltip + accessible name); the
    // webhook gate's reason wins over the clean one.
    const reason = locked ? this.constructor.WEBHOOK_GATE_HINT : (clean ? SAVE_UNCHANGED_REASON : null)
    if (reason) {
      save.setAttribute("data-gl-tooltip", reason)
      save.setAttribute("aria-label", `${save.textContent} — ${reason}`)
    } else {
      save.removeAttribute("data-gl-tooltip")
      save.removeAttribute("aria-label")
    }
  }

  // The first live form this edit session is the baseline; later ones compare.
  #dirty(form) {
    const current = formFingerprint(form)
    if (this._baseline == null) this._baseline = current
    return current !== this._baseline
  }

  #syncSlack(form, editing) {
    const source = editing ? this.#formSlackButton(form) : null
    const proxy = this.slackButtonTarget
    proxy.hidden = !source
    if (source) proxy.textContent = source.textContent.trim()
  }

  // The frame host (a body-only wrapper); falls back to the modal body so a
  // shell without the wrapper still observes body changes, never the footer.
  #observedRoot() {
    if (this.hasFrameHostTarget) return this.frameHostTarget
    return this.element.querySelector(".gl-modal-body") || this.element
  }

  // Always the LIVE frame: a 422 replaces the node, so nothing may cache it.
  #frame() {
    return this.#observedRoot().querySelector(`#${this.constructor.FRAME_ID}`)
  }

  #form() {
    return this.#frame()?.querySelector("form") || null
  }

  #formSlackButton(form = this.#form()) {
    return form?.querySelector('[data-controller~="slack-channel-create"]') || null
  }
}
